import { cn } from "@/utils/utils";
import { Cell, flexRender, Row, ColumnOrderState } from "@tanstack/react-table";
import React from "react";
import TruncatedCell from "./TruncatedCell";

interface TableRowProps<T> {
  row: Row<T>;
  onRowClick?: (row: Row<T>) => void;
  groupBy: string[];
  showRowHover: (row: Row<T>) => boolean;
  columnOrder: ColumnOrderState;
  stickyLeftColumn?: boolean;
  selectedRows?: Set<string>;
  getRowClassName?: (row: Row<T>) => string | undefined;
}

// The column meta keys this row reads; tables set them per column.
type CellMeta = {
  hideSeparator?: boolean;
  textAlign?: string;
  paddingLeft?: string;
  disableTooltip?: boolean;
};

function TableRow<T>({
  row,
  onRowClick,
  groupBy,
  showRowHover = () => true,
  stickyLeftColumn,
  columnOrder,
  selectedRows,
  getRowClassName
}: TableRowProps<T>) {
  const orderedCells = row
    .getAllCells()
    .reduce<Record<string, Cell<T, unknown>>>((acc, cell) => {
      acc[cell.column.id] = cell;
      return acc;
    }, {});

  const isHoverable = showRowHover(row);
  const isSelected =
    selectedRows?.has((row.original as { id: string }).id) || false;

  let visibleIndex = 0;
  return (
    <tr
      className={cn("group", getRowClassName?.(row))}
      onClick={() => onRowClick?.(row)}
    >
      {columnOrder.map((columnId) => {
        const cell = orderedCells[columnId];

        if (!cell || !cell.column.getIsVisible() || groupBy?.includes(columnId))
          return null;

        const renderedIndex = visibleIndex++;
        const isStickyLeftColumn = stickyLeftColumn && renderedIndex === 0;
        const columnSize = cell.column.getSize();
        const hideSeparator =
          (cell.column.columnDef.meta as CellMeta | undefined)
            ?.hideSeparator === true;

        return (
          <td
            key={cell.id}
            className={cn(
              "relative px-2 py-2 border-b border-border-border1 text-foreground text-base select-none",
              `text-${(cell.column.columnDef.meta as CellMeta | undefined)?.textAlign ?? "start"}`,
              (cell.column.columnDef.meta as CellMeta | undefined)
                ?.paddingLeft ?? "pl-5",
              cell.column.getIsLastColumn() ? "pr-4" : "",
              isHoverable && onRowClick && "cursor-pointer",
              isHoverable &&
                !isStickyLeftColumn &&
                !isSelected &&
                "group-hover:bg-surface-elevation2",
              isStickyLeftColumn &&
                isHoverable &&
                !isSelected &&
                "sticky-hover-cell",
              isStickyLeftColumn
                ? `sticky bg-surface-elevation0 left-0 z-10${!hideSeparator ? " after:content-[''] after:absolute after:top-0 after:right-0 after:bottom-0 after:w-px after:bg-border-border1" : ""}`
                : "",
              { "pl-8": cell.column.id === groupBy[0] },
              isSelected &&
                "!bg-surface-focus-selected border-t border-b !border-border-border2"
            )}
            style={{
              width: columnSize,
              minWidth: columnSize,
              maxWidth: columnSize
            }}
          >
            {cell.column.id === "actions" ||
            (cell.column.columnDef.meta as CellMeta | undefined)
              ?.disableTooltip ? (
              <div className="truncate">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            ) : (
              <TruncatedCell
                content={flexRender(
                  cell.column.columnDef.cell,
                  cell.getContext()
                )}
                className="truncate"
              />
            )}
          </td>
        );
      })}
    </tr>
  );
}

export const MemoizedTableRow = React.memo(TableRow) as typeof TableRow;
